/**
 * Electron Download Utility
 * Handles file downloads with role-specific paths when running in Electron
 */
import Config from "../Config";
import { getRole, getUser } from "./authStorage";

/**
 * Get user role from auth storage (sessionStorage, per-tab)
 */
export const getUserRole = () => {
  const role = getRole() || '';
  const roleUpper = role.toUpperCase();
  
  // Map roles to L1, L2, L3
  if (roleUpper === 'TRANSCRIPTIONIST' || roleUpper === 'MT') {
    return 'L1';
  } else if (roleUpper === 'EDITOR') {
    return 'L2';
  } else if (roleUpper === 'QA' || roleUpper === 'QUALITY ASSURANCE') {
    return 'L3';
  }
  
  // Try to get from user object
  const user = getUser();
  if (user) {
    const userRoles = user.UserRoles || [];
    if (userRoles.length > 0) {
      const roleName = (userRoles[0].RoleName || '').toUpperCase();
      if (roleName === 'TRANSCRIPTIONIST' || roleName === 'MT') {
        return 'L1';
      } else if (roleName === 'EDITOR') {
        return 'L2';
      } else if (roleName === 'QA' || roleName === 'QUALITY ASSURANCE') {
        return 'L3';
      }
    }
  }
  
  return null;
};

/**
 * Check if running in Electron
 */
export const isElectron = () => {
  return typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron;
};

/**
 * Download file using Electron API (saves to role-specific path)
 */
export const downloadFileElectron = async (url, fileName, token = null) => {
  if (!isElectron()) {
    throw new Error('Not running in Electron');
  }
  
  const userRole = getUserRole();
  if (!userRole) {
    throw new Error('User role not found');
  }
  
  try {
    const result = await window.electronAPI.downloadFile({
      url,
      fileName,
      userRole,
      token,
    });
    
    if (!result.success) {
      throw new Error(result.error || 'Download failed');
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Save blob file using Electron API
 */
export const saveBlobFileElectron = async (blob, fileName) => {
  if (!isElectron()) {
    throw new Error('Not running in Electron');
  }
  
  const userRole = getUserRole();
  if (!userRole) {
    throw new Error('User role not found');
  }
  
  try {
    // Convert blob to base64
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1]; // Remove data:type;base64, prefix
        resolve(base64);
      };
      reader.onerror = reject;
    });
    
    reader.readAsDataURL(blob);
    const blobData = await base64Promise;
    
    const result = await window.electronAPI.saveBlobFile({
      blobData,
      fileName,
      userRole,
    });
    
    if (!result.success) {
      throw new Error(result.error || 'Save failed');
    }
    
    // Log success with file path
    if (result.filePath) {
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};

/**
 * Download file (fallback to browser download if not Electron)
 */
export const downloadFile = async (url, fileName, token = null) => {
  if (isElectron()) {
    try {
      return await downloadFileElectron(url, fileName, token);
    } catch (error) {
      // Fall through to browser download
    }
  }
  
  // Browser download fallback
  try {
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    
    if (!response.ok) {
      throw new Error(`Failed to download: ${response.status}`);
    }
    
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    
    return { success: true };
  } catch (error) {
    throw error;
  }
};

/**
 * Save blob file (fallback to browser download if not Electron)
 */
export const saveBlobFile = async (blob, fileName) => {
  if (isElectron()) {
    try {
      const result = await saveBlobFileElectron(blob, fileName);
      // Show success message with file path
      if (result.success && result.filePath) {
      }
      return result;
    } catch (error) {
      // In Electron, don't fall back to browser download - show error instead
      throw new Error(`Failed to save file: ${error.message}`);
    }
  }
  
  // Browser download fallback (only if not in Electron)
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
  
  return { success: true };
};

/**
 * Show notification with sound
 */
export const showNotification = async (title, body, sound = true) => {
  if (isElectron() && window.electronAPI.showNotification) {
    try {
      await window.electronAPI.showNotification(title, body, sound);
      return { success: true };
    } catch (error) {
    }
  }
  
  // Fallback to browser notification
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: Config.asset('logo-removebg-preview.png'),
    });
    return { success: true };
  } else if ('Notification' in window && Notification.permission !== 'denied') {
    Notification.requestPermission().then((permission) => {
      if (permission === 'granted') {
        new Notification(title, {
          body,
          icon: Config.asset('logo-removebg-preview.png'),
        });
      }
    });
  }
  
  return { success: false };
};
